import { 
  collection, 
  doc, 
  addDoc, 
  deleteDoc, 
  getDocs, 
  query, 
  where,
  Timestamp
} from 'firebase/firestore';
import { db } from './firebase';

const COLLECTIONS = {
  SAVED_JOBS: 'savedJobs'
};

/**
 * Save a job for a user
 * @param {string} userId - ID of the user
 * @param {string} jobId - ID of the job to save
 * @returns {Object} Result object with success status
 */
export const saveJob = async (userId, jobId) => {
  try {
    // Check if job is already saved
    const existingSave = await isJobSaved(userId, jobId);
    if (existingSave.isSaved) {
      return { success: false, error: 'Job is already saved' };
    }

    const savedJobData = {
      userId,
      jobId,
      savedAt: Timestamp.now()
    };

    const docRef = await addDoc(collection(db, COLLECTIONS.SAVED_JOBS), savedJobData);
    
    return { 
      success: true, 
      savedJobId: docRef.id,
      message: 'Job saved successfully'
    };
  } catch (error) {
    console.error('Error saving job:', error);
    return { 
      success: false, 
      error: error.message 
    };
  }
};

/**
 * Remove a saved job
 * @param {string} userId - ID of the user
 * @param {string} jobId - ID of the job to unsave
 * @returns {Object} Result object with success status
 */
export const unsaveJob = async (userId, jobId) => {
  try {
    const q = query(
      collection(db, COLLECTIONS.SAVED_JOBS),
      where('userId', '==', userId),
      where('jobId', '==', jobId)
    );
    
    const querySnapshot = await getDocs(q);
    
    if (querySnapshot.empty) {
      return { success: false, error: 'Saved job not found' };
    }

    // Delete all matching documents (should be only one)
    const deletePromises = querySnapshot.docs.map(doc => deleteDoc(doc.ref));
    await Promise.all(deletePromises);
    
    return { 
      success: true,
      message: 'Job removed from saved jobs'
    };
  } catch (error) {
    console.error('Error unsaving job:', error);
    return { 
      success: false, 
      error: error.message 
    };
  }
};

/**
 * Check if a job is saved by a user
 * @param {string} userId - ID of the user
 * @param {string} jobId - ID of the job to check
 * @returns {Object} Result object with isSaved status
 */
export const isJobSaved = async (userId, jobId) => {
  try {
    const q = query(
      collection(db, COLLECTIONS.SAVED_JOBS),
      where('userId', '==', userId),
      where('jobId', '==', jobId)
    );
    
    const querySnapshot = await getDocs(q);
    
    return { 
      success: true,
      isSaved: !querySnapshot.empty,
      savedJobId: querySnapshot.empty ? null : querySnapshot.docs[0].id
    };
  } catch (error) {
    console.error('Error checking if job is saved:', error);
    return { 
      success: false, 
      error: error.message,
      isSaved: false
    };
  }
};

/**
 * Get all saved jobs for a user
 * @param {string} userId - ID of the user
 * @returns {Object} Result object with saved jobs array
 */
export const getSavedJobs = async (userId) => {
  try {
    const q = query(
      collection(db, COLLECTIONS.SAVED_JOBS),
      where('userId', '==', userId)
    );
    
    const querySnapshot = await getDocs(q);
    const savedJobs = querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
      savedAt: doc.data().savedAt?.toDate()
    }));
    
    return { 
      success: true, 
      savedJobs: savedJobs.sort((a, b) => b.savedAt - a.savedAt) // Sort by most recent first
    };
  } catch (error) {
    console.error('Error fetching saved jobs:', error);
    return { 
      success: false, 
      error: error.message,
      savedJobs: []
    };
  }
};

/**
 * Get saved job IDs for a user (for quick lookup)
 * @param {string} userId - ID of the user
 * @returns {Object} Result object with array of job IDs
 */
export const getSavedJobIds = async (userId) => {
  try {
    const result = await getSavedJobs(userId);
    if (result.success) {
      const jobIds = result.savedJobs.map(savedJob => savedJob.jobId);
      return { success: true, jobIds };
    }
    return { success: false, error: result.error, jobIds: [] };
  } catch (error) {
    console.error('Error fetching saved job IDs:', error);
    return { 
      success: false, 
      error: error.message,
      jobIds: []
    };
  }
};
